import { useMemo } from "react";
import { formatCurrency } from "@vaulzo/core/constants";
import { GROUPS, groupTotals } from "@vaulzo/core/domain/groups";

/**
 * "What you own": one bar and one row per group of assets, as a share of
 * everything owned. Each row opens the matching Assets tab. The grouping
 * (and where broker cash goes) is shared with the Assets page.
 */
export default function WhatYouOwn({ assets = [], onSelect }) {
  const { rows, total } = useMemo(() => {
    const sums = groupTotals(assets);
    const total = Object.values(sums).reduce((s, v) => s + v, 0);
    const rows = GROUPS.filter((g) => sums[g.key] > 0).map((g) => ({
      ...g,
      value: sums[g.key],
      pct: total ? (sums[g.key] / total) * 100 : 0,
    }));
    return { rows, total };
  }, [assets]);

  if (rows.length === 0) return null;

  return (
    <section>
      <div className="flex items-baseline justify-between">
        <span className="text-[12px] uppercase tracking-[0.14em] text-vaulzo-muted">What you own</span>
        <span className="num text-[13px] font-semibold text-vaulzo-ink">{formatCurrency(total)}</span>
      </div>

      <div aria-hidden className="mt-[14px] flex h-3 gap-[2px] overflow-hidden rounded-md">
        {rows.map((r) => (
          <div key={r.key} style={{ width: `${r.pct}%`, background: r.color }} />
        ))}
      </div>

      <div className="mt-[14px] overflow-hidden rounded-2xl border border-vaulzo-hairline bg-vaulzo-surface">
        {rows.map((r, i) => (
          <button
            key={r.key}
            type="button"
            onClick={() => onSelect?.(r.key)}
            className={`flex w-full items-center gap-3 px-[18px] py-[14px] text-left transition-colors hover:bg-vaulzo-bg ${
              i < rows.length - 1 ? "border-b border-vaulzo-row" : ""
            }`}
          >
            <span aria-hidden className="h-[10px] w-[10px] flex-none rounded-[3px]" style={{ background: r.color }} />
            <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
              <span className="text-[15px] font-semibold text-vaulzo-ink">{r.short}</span>
              <span className="text-[12px] text-vaulzo-muted">{r.sub}</span>
            </span>
            <span className="num text-[12px] text-vaulzo-muted">{r.pct.toFixed(1)}%</span>
            <span className="num text-[15px] font-bold text-vaulzo-ink">{formatCurrency(r.value)}</span>
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden className="flex-none">
              <path d="M5 3l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ))}
      </div>
    </section>
  );
}
